import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { Spinner } from '../../components/Spinner';
import { formatNumber, formatRelative, shortUrlFor } from '../../lib/format';
import type { ShortLink } from '@shared/types';

export function AdminLinksPage() {
  const [data, setData] = useState<{ items: ShortLink[]; total: number } | null>(null);
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => {
      const params = new URLSearchParams();
      params.set('pageSize', '50');
      if (search.trim()) params.set('search', search.trim());
      api
        .get<{ items: ShortLink[]; total: number }>(`/api/admin/links?${params.toString()}`)
        .then(setData)
        .catch((err) => setError(err instanceof Error ? err.message : String(err)));
    }, 200);
    return () => clearTimeout(t);
  }, [search]);

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-bold tracking-tight">All links</h1>
        <p className="text-sm text-ink-500 dark:text-ink-400">
          Search and inspect every short link across the platform.
        </p>
      </header>
      <input
        className="input max-w-md"
        placeholder="Search by code, title, destination..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      {error && (
        <div className="rounded-lg bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-200 px-4 py-3 text-sm">
          {error}
        </div>
      )}
      {!data && (
        <div className="flex items-center justify-center py-16">
          <Spinner size={28} />
        </div>
      )}
      {data && (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Destination</th>
                <th>Org</th>
                <th>Clicks</th>
                <th>Status</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((l) => (
                <tr key={l.id}>
                  <td className="font-mono text-xs">
                    <a
                      href={shortUrlFor(l.code)}
                      target="_blank"
                      rel="noreferrer"
                      className="text-brand-600 dark:text-brand-300"
                    >
                      {l.code}
                    </a>
                  </td>
                  <td className="truncate max-w-md">{l.destinationUrl}</td>
                  <td className="text-xs text-ink-500 dark:text-ink-400">
                    {l.organizationId.slice(0, 8)}…
                  </td>
                  <td>{formatNumber(l.clickCount)}</td>
                  <td>
                    <span
                      className={l.status === 'active' ? 'badge-success' : 'badge-muted'}
                    >
                      {l.status}
                    </span>
                  </td>
                  <td className="text-xs text-ink-500 dark:text-ink-400">
                    {formatRelative(l.createdAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
